import { Box, Button, Flex, Text } from '@chakra-ui/react'
import { Editor, ReactRenderer } from '@tiptap/react'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import tippy from 'tippy.js'

import { PersonaType } from '@/modules/auth/domain/models/Persona'
import AvatarActivity from '@/shared/components/avatar-activity'
import { MentionedType } from '@/shared/types/post/enums/MentionedType'
import MyGroupIcon from '@/ui/assets/MyGroupIcon'
import PersonsMyGroupIcon from '@/ui/assets/PersonsMyGroupIcon'

import { getMarkdownMentionStorage } from '../utils/getMarkdownMentionStorage'

const MentionList = forwardRef(function MentionList(
  {
    editor,
    command,
    mentionKey
  }: {
    editor: Editor
    command: (props: { id: string }) => void
    mentionKey: string
  },
  ref
) {
  const { t } = useTranslation()
  const [selectedIndex, setSelectedIndex] = useState(0)

  const storage = getMarkdownMentionStorage(editor, mentionKey)

  const selectItem = (index: number) => {
    const item = storage.suggestions[index]
    if (!item) return
    const [id, , personaType, avatarName] = item.split(',')
    command({
      id: `${
        personaType === 'GROUP'
          ? MentionedType.enum.GROUP
          : personaType === 'ACCESS_PROFILE'
            ? MentionedType.enum.ACCESS_PROFILE
            : MentionedType.enum.PERSON
      }/${id}/${avatarName}`
    })
    storage.selected = {
      id,
      name: avatarName,
      avatarName
    }
  }

  useEffect(() => setSelectedIndex(0), [storage.suggestions])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex(
          (selectedIndex + storage.suggestions.length - 1) %
            storage.suggestions.length
        )
        return true
      }

      if (event.key === 'ArrowDown') {
        setSelectedIndex((selectedIndex + 1) % storage.suggestions.length)
        return true
      }

      if (event.key === 'Enter') {
        selectItem(selectedIndex)
        return true
      }

      return false
    }
  }))

  return (
    <Flex
      flexDirection={'column'}
      bg={'white'}
      maxH={'300px'}
      overflowY={'auto'}
    >
      {storage.suggestions.length ? (
        storage.suggestions.map((item, i) => {
          const [id, fullName, personaType, avatarName, avatarUrl] =
            item.split(',')

          const isGroupMention = personaType === 'GROUP'
          const isAccessProfileMention = personaType === 'ACCESS_PROFILE'

          return (
            <Box
              key={id}
              onClick={() => selectItem(i)}
              bgColor={i === selectedIndex ? 'blue.200' : undefined}
              mt={'8px'}
              paddingLeft={'5px'}
              paddingRight={'5px'}
              paddingTop={'2px'}
              paddingBottom={'2px'}
            >
              <Flex gap={'16px'} mb={'4px'} mt={'4px'} alignItems={'center'}>
                {isGroupMention ? (
                  <Box
                    backgroundColor={'#DEE2E6'}
                    p={'1'}
                    borderRadius={'full'}
                  >
                    <MyGroupIcon
                      w={'24px'}
                      h={'24px'}
                      p={'1px'}
                      strokeWidth={1}
                      color={'#000'}
                    />
                  </Box>
                ) : isAccessProfileMention ? (
                  <Box
                    backgroundColor={'custom.blue.light'}
                    p={'1'}
                    borderRadius={'full'}
                  >
                    <PersonsMyGroupIcon
                      w={'24px'}
                      h={'24px'}
                      p={'1px'}
                      strokeWidth={1}
                      color={'custom.blue.darker'}
                    />
                  </Box>
                ) : (
                  <AvatarActivity
                    activity={'gray'}
                    size={'sm'}
                    imageUrl={avatarUrl}
                    name={fullName.trim()}
                  />
                )}
                <Flex flexDir={'column'}>
                  <Flex flexDir={'column'}>
                    <Text fontSize="16px" fontWeight="500" color="#212529">
                      {avatarName}
                    </Text>
                    <Text fontSize={'0.75rem'} fontWeight={'400'}>
                      {fullName}
                    </Text>
                  </Flex>
                  {!isGroupMention && !isAccessProfileMention && (
                    <Text fontSize={'0.75rem'} fontWeight={'400'}>
                      {t(
                        `profile.profileType.${
                          personaType.toLowerCase() as Lowercase<PersonaType>
                        }`
                      )}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Box>
          )
        })
      ) : (
        <Button isDisabled border={'solid 1px gray'}>
          <Text>{t('general.common.terms.empty')}</Text>
        </Button>
      )}
    </Flex>
  )
})

export default function MentionSuggestionRender({
  mentionKey
}: {
  mentionKey: string
}) {
  return () => MentionSuggestionRenderComponent({ mentionKey })
}

function MentionSuggestionRenderComponent({
  mentionKey
}: {
  mentionKey: string
}) {
  let reactRenderer
  let popup

  return {
    onStart: (props) => {
      if (!props.clientRect) {
        return
      }

      reactRenderer = new ReactRenderer(MentionList, {
        props: { ...props, mentionKey },
        editor: props.editor
      })

      popup = tippy('body', {
        getReferenceClientRect: props.clientRect,
        appendTo: () => document.body,
        content: reactRenderer.element,
        showOnCreate: true,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start'
      })
    },

    onUpdate(props) {
      reactRenderer.updateProps({ ...props, mentionKey })

      if (!props.clientRect) {
        return
      }

      popup?.[0]?.setProps({
        getReferenceClientRect: props.clientRect
      })
    },

    onKeyDown(props) {
      if (props.event.key === 'Escape') {
        popup?.[0]?.hide()

        return true
      }

      return reactRenderer.ref?.onKeyDown(props)
    },

    onExit() {
      popup?.[0]?.destroy()
      reactRenderer?.destroy()
    }
  }
}
